import { t } from "@lingui/core/macro";
/* eslint-disable class-methods-use-this */
import { EMPTY_ARRAY, EMPTY_STRING, noop } from "@regrello/core-utils";
import { DataTestIds } from "@regrello/data-test-ids-api";
import { FeatureFlagKey } from "@regrello/feature-flags-api/src/featureFlagKey";
import type {
  FieldFields,
  FieldInstanceFields,
  PropertyTypeFields,
  SpectrumFieldConstraintFields,
  SpectrumFieldValidationTypeFields,
  SpectrumFieldVersionFields,
  SpectrumValueConstraintFields,
  UpdateFieldInstanceValueInputs,
} from "@regrello/graphql-api";
import { RegrelloLinkV2 } from "@regrello/ui-core";
import type { ColDef, ValueFormatterParams } from "ag-grid-community";
import type { ReactNode } from "react";
import type { FieldArrayWithId, FieldPath, FieldValues, UseFormReturn } from "react-hook-form";
import { ValidationRules } from "../../../../../../constants/globalConstants";
import { FeatureFlagService } from "../../../../../../services/FeatureFlagService";
import type { ConfigureSpectrumFieldFormFormFields } from "../../../../../../ui2/fields/configure/_internal/ConfigureSpectrumFieldForm";
import type { RowDatum } from "../../../../../../ui2/forms/instance/utils/tableV2Utils";
import type {
  CustomFieldPlugin,
  CustomFieldPluginV2RenderFormFieldProps,
  ValidateBulkUploadCellValueOptions,
} from "../../../customFields/plugins/types/CustomFieldPlugin";
import {
  type ValidationResult,
  validateRequired,
} from "../../../customFields/plugins/utils/validateBulkUploadCellValueUtils";
import { RegrelloControlledFormFieldNumber } from "../../../formFields/controlled/RegrelloControlledFormFieldNumber";
import { RegrelloFormFieldSwitch } from "../../../formFields/RegrelloFormFieldSwitch";
import { RegrelloFormFieldText } from "../../../formFields/RegrelloFormFieldText";
import type { ProspectiveSpectrumFieldVersion } from "../../types/ProspectiveSpectrumFieldVersion";
import { SpectrumFieldPluginDecorator } from "../../types/SpectrumFieldPluginDecorator";
import { SpectrumFieldValidationType } from "../../types/SpectrumFieldValidationType";
import {
  FrontendValueConstraintRuleName,
  getValueConstraintDisplayName,
  validateByConstraints,
} from "../../utils/spectrumFieldConstraintUtils";
import { RegrelloEditableCellText } from "./_internal/RegrelloEditableCellText";
import { RegexValidatedConstraintField } from "./_internal/RegrelloValidatedConstraintField";
import type { TextFieldPluginFrontendValue } from "./_internal/types";

type VALUE = TextFieldPluginFrontendValue;

/** The unique identifier for the text field plugin. */
export const SPECTRUM_TEXT_FIELD_PLUGIN_URI = "com.regrello.spectrumField.text";

export class SpectrumTextFieldPluginDecorator extends SpectrumFieldPluginDecorator<VALUE> {
  constructor(plugin: CustomFieldPlugin<VALUE>) {
    super(plugin);
    this.uri = SPECTRUM_TEXT_FIELD_PLUGIN_URI;
  }

  public canProcessValidationType(spectrumValidationType: ProspectiveSpectrumFieldVersion["validationType"]) {
    return spectrumValidationType.validationType === SpectrumFieldValidationType.RAW;
  }

  public canProcessSpectrumField(field: ProspectiveSpectrumFieldVersion) {
    return (
      this.canProcessPropertyDataType(field.propertyType.dataType) &&
      this.canProcessValidationType(field.validationType)
    );
  }

  public findPropertyTypeFromLoadedPropertyTypes(propertyTypes: PropertyTypeFields[]) {
    return propertyTypes.find((propertyType) => propertyType.dataType === "STRING");
  }

  public findValidationTypeFromLoadedValidationTypes(validationTypes: SpectrumFieldValidationTypeFields[]) {
    return validationTypes.find((validationType) => validationType.validationType === SpectrumFieldValidationType.RAW);
  }

  public findValueConstraintsFromLoadedValueConstraints(valueConstraints: SpectrumValueConstraintFields[]) {
    return valueConstraints.filter(
      ({ valueConstraintRule }) =>
        valueConstraintRule === FrontendValueConstraintRuleName.MAX_CHARACTER ||
        valueConstraintRule === FrontendValueConstraintRuleName.MIN_CHARACTER ||
        valueConstraintRule === FrontendValueConstraintRuleName.MARKDOWN_FORMATTED ||
        valueConstraintRule === FrontendValueConstraintRuleName.REGEX_VALIDATED,
    );
  }

  public getConstraintConditionOperators() {
    return {
      IS_ZERO: {
        label: t`Is empty`,
        inputCount: 0,
        isMultiselect: false,
      },
      IS_NOT_ZERO: {
        label: t`Is not empty`,
        inputCount: 0,
        isMultiselect: false,
      },
      EQ: {
        label: t`Is equal to`,
        inputCount: 1,
        isMultiselect: false,
      },
      NOT: {
        label: t`Is not equal to`,
        inputCount: 1,
        isMultiselect: false,
      },
      STRING_CONTAINS: {
        label: t`Contains`,
        inputCount: 1,
        isMultiselect: false,
      },
      STRING_NOT_CONTAINS: {
        label: t`Does not contain`,
        inputCount: 1,
        isMultiselect: false,
      },
      STRING_STARTS_WITH: {
        label: t`Starts with`,
        inputCount: 1,
        isMultiselect: false,
      },
      STRING_ENDS_WITH: {
        label: t`Ends with`,
        inputCount: 1,
        isMultiselect: false,
      },
    };
  }

  public getSpectrumFormAutosaveMode() {
    return "onBlur" as const;
  }

  public getConstraintsToDisplay(field: SpectrumFieldVersionFields): SpectrumFieldConstraintFields[] {
    return field.fieldConstraints;
  }

  public override renderSpectrumFormField = <TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>(
    field: FieldFields,
    props: CustomFieldPluginV2RenderFormFieldProps<TFieldValues, TName>,
  ): React.ReactNode => {
    return this.renderFormField(props.fieldInstance?.spectrumFieldVersion.field ?? field, props);
  };

  public renderPreviewFormField() {
    return <RegrelloFormFieldText disabled={true} onChange={noop} size="medium" value="" />;
  }

  public renderValueConstraints(props: {
    constraints: Array<FieldArrayWithId<ConfigureSpectrumFieldFormFormFields, "valueConstraints">>;
    disabled: boolean;
    form: UseFormReturn<ConfigureSpectrumFieldFormFormFields>;
    updateConstraint: (index: number, value: ConfigureSpectrumFieldFormFormFields["valueConstraints"][number]) => void;
  }): ReactNode {
    const { constraints, disabled, form, updateConstraint } = props;
    const getFieldKey = (index: number) => `valueConstraints.${index}.args.0` as const;

    const minCharacterConstraintIndex = constraints.findIndex(
      (constraint) => constraint.constraint.valueConstraintRule === FrontendValueConstraintRuleName.MIN_CHARACTER,
    );
    const maxCharacterConstraintIndex = constraints.findIndex(
      (constraint) => constraint.constraint.valueConstraintRule === FrontendValueConstraintRuleName.MAX_CHARACTER,
    );

    const markdownFormattedConstraintIndex = constraints.findIndex(
      (constraint) => constraint.constraint.valueConstraintRule === FrontendValueConstraintRuleName.MARKDOWN_FORMATTED,
    );

    const regexValidatedConstraintIndex = constraints.findIndex(
      (constraint) => constraint.constraint.valueConstraintRule === FrontendValueConstraintRuleName.REGEX_VALIDATED,
    );

    if (minCharacterConstraintIndex < 0 || maxCharacterConstraintIndex < 0) {
      return null;
    }
    const minCharacterConstraintArg = form.getValues(`valueConstraints.${minCharacterConstraintIndex}.args.0`);
    const maxCharacterConstraintArg = form.getValues(`valueConstraints.${maxCharacterConstraintIndex}.args.0`);

    const typedMinCharacterConstraintArg = minCharacterConstraintArg
      ? Number.parseInt(minCharacterConstraintArg, 10)
      : Number.MIN_SAFE_INTEGER;
    const typedMaxCharacterConstraintArg = maxCharacterConstraintArg
      ? Number.parseInt(maxCharacterConstraintArg, 10)
      : Number.MAX_SAFE_INTEGER;

    return (
      <>
        {constraints.map((row, constraintIndex) => {
          const fieldKey = getFieldKey(constraintIndex);
          if (constraintIndex === markdownFormattedConstraintIndex) {
            return (
              <RegrelloFormFieldSwitch
                key={row.constraint.uuid}
                checked={form.getValues(fieldKey) === "true"}
                dataTestId={DataTestIds.CREATE_FIELD_DIALOG_FIELD_CONSTRAINT}
                disabled={disabled}
                helperText={
                  <>
                    {t`Format text with Markdown if enabled. `}
                    <RegrelloLinkV2
                      className="mb-10 font-normal text-xs"
                      to={"https://www.markdownguide.org/basic-syntax/"}
                    >
                      {t`Learn more.`}
                    </RegrelloLinkV2>
                  </>
                }
                label={getValueConstraintDisplayName(row.constraint)}
                onChange={(isChecked) => {
                  const previousValue = form.getValues(`valueConstraints.${constraintIndex}`);
                  if (isChecked) {
                    // eslint-disable-next-line lingui/no-unlocalized-strings
                    updateConstraint(constraintIndex, { ...previousValue, args: ["true"] });
                  } else {
                    // eslint-disable-next-line lingui/no-unlocalized-strings
                    updateConstraint(constraintIndex, { ...previousValue, args: ["false"] });
                  }
                }}
                variant="spectrum"
              />
            );
          }
          if (constraintIndex === regexValidatedConstraintIndex) {
            return FeatureFlagService.isEnabled(FeatureFlagKey.FIELD_VALIDATION_REGEX_2026_01) ? (
              <RegexValidatedConstraintField
                key={row.constraint.uuid}
                constraintIndex={constraintIndex}
                disabled={disabled}
                form={form}
                row={row}
                updateConstraint={updateConstraint}
              />
            ) : undefined;
          }
          return (
            <RegrelloControlledFormFieldNumber
              key={row.constraint.uuid}
              controllerProps={{
                control: form.control,
                name: fieldKey,
                rules:
                  constraintIndex === minCharacterConstraintIndex && maxCharacterConstraintArg != null
                    ? {
                        ...ValidationRules.INTEGER,
                        ...ValidationRules.LESS_THAN_OR_EQUAL_TO_UPPER_BOUND(typedMaxCharacterConstraintArg),
                        ...ValidationRules.GREATER_THAN_OR_EQUAL_TO_LOWER_BOUND(Number.MIN_SAFE_INTEGER),
                      }
                    : constraintIndex === maxCharacterConstraintIndex && minCharacterConstraintArg != null
                      ? {
                          ...ValidationRules.INTEGER,
                          ...ValidationRules.LESS_THAN_OR_EQUAL_TO_UPPER_BOUND(Number.MAX_SAFE_INTEGER),
                          ...ValidationRules.GREATER_THAN_OR_EQUAL_TO_LOWER_BOUND(typedMinCharacterConstraintArg),
                        }
                      : {
                          ...ValidationRules.INTEGER,
                          ...ValidationRules.LESS_THAN_OR_EQUAL_TO_UPPER_BOUND(Number.MAX_SAFE_INTEGER),
                          ...ValidationRules.GREATER_THAN_OR_EQUAL_TO_LOWER_BOUND(Number.MIN_SAFE_INTEGER),
                        },
              }}
              dataTestId={DataTestIds.CREATE_FIELD_DIALOG_FIELD_CONSTRAINT}
              disabled={disabled}
              label={getValueConstraintDisplayName(row.constraint)}
            />
          );
        })}
      </>
    );
  }

  public getColumnDef<TData extends RowDatum>(
    colDefParams: Partial<ColDef<TData, VALUE>>,
    options: {
      fieldInstance: FieldInstanceFields;
      placeholderRenderer?: (params: ValueFormatterParams<TData, VALUE>) => ReactNode;
      textFormat?: "clip" | "wrap";
    },
  ): ColDef<TData, VALUE> {
    const isTextWrappingEnabled = options.textFormat === "wrap";

    const wrapTextParams = {
      autoHeight: isTextWrappingEnabled,
      wrapText: isTextWrappingEnabled,
    };

    return {
      ...colDefParams,
      cellClassRules: {
        ...colDefParams.cellClassRules,
        "regrello-text-wrapped-cell": (_) => isTextWrappingEnabled,
        "rg-grid-not-editable": (params) => {
          // (max): gray cell out if it is not editable.
          if (params.colDef.editable == null) {
            return true;
          }
          if (typeof params.colDef.editable === "boolean") {
            return !params.colDef.editable;
          }
          return !params.colDef.editable(params);
        },
      },
      cellEditor: RegrelloEditableCellText,
      cellEditorParams: {
        fieldInstance: options.fieldInstance,
      },
      cellEditorPopup: true,
      cellEditorPopupPosition: "over" as const,
      cellRenderer: (params: ValueFormatterParams<TData, VALUE>): ReactNode => {
        if (params.value == null || params.value === "") {
          return options.placeholderRenderer ? options.placeholderRenderer(params) : "";
        }
        return String(params.value);
      },
      ...wrapTextParams,
    };
  }

  public getFieldUpdateInputsFromFrontendValue(
    fieldInstance: FieldInstanceFields,
    frontendValue: TextFieldPluginFrontendValue,
  ): UpdateFieldInstanceValueInputs {
    return {
      id: fieldInstance.values[0].id,
      inputType: fieldInstance.values[0].inputType,
      stringValue: frontendValue,
    };
  }

  public isValidTableColumn(_: SpectrumFieldVersionFields): boolean {
    return true;
  }

  public validateBulkUploadCellValue(
    _field: FieldFields,
    value: string,
    options: ValidateBulkUploadCellValueOptions,
  ): ValidationResult {
    const normalizedString = value.trim();

    const result = validateRequired(normalizedString, options.isRequired);
    if (result != null) {
      return result;
    }

    const spectrumValidationResult = this.validateValueInternal(options.fieldVersion, normalizedString);
    if (!spectrumValidationResult.isValid) {
      return {
        value: normalizedString,
        info: spectrumValidationResult.errors.map((errorString) => ({
          message: errorString,
          level: "error" as const,
        })),
      };
    }

    return {
      value: normalizedString,
    };
  }

  public validateValue(
    fieldVersion: SpectrumFieldVersionFields,
    value: unknown,
    isRequired: boolean,
  ): { isValid: boolean; errors: string[] } {
    if (value == null || value === EMPTY_STRING) {
      if (isRequired) {
        return { isValid: false, errors: [t`This field is required`] };
      }
      return { isValid: true, errors: EMPTY_ARRAY };
    }

    if (typeof value !== "string") {
      console.error("Invalid value type for text field plugin. This is a developer error.", value);
      return { isValid: false, errors: [t`Must be a string`] };
    }

    return this.validateValueInternal(fieldVersion, value);
  }

  private validateValueInternal(
    fieldVersion: SpectrumFieldVersionFields,
    value: VALUE,
  ): { isValid: boolean; errors: string[] } {
    return validateByConstraints(fieldVersion.fieldConstraints, value);
  }
}
